import {useEffect} from "react";
import {shallowEqual, useDispatch, useSelector} from "react-redux";
import {useHistory, useLocation} from "react-router";
import {isEqual, isNull, omit, pick} from "lodash";
import {Dispatch} from "redux";
import {appendQueryString, IQueryValue, parseSearch} from "@web2/ap-request";
import {RequestState} from "@web2/ap-request-state";

interface FetchableStore {
    requestState: RequestState;
    latestQuery: Record<string, unknown>;
}

export const useFetchOnQueryChange = <T>(
    stateSelector: (store: T) => FetchableStore,
    fetchAction: (query: Record<string, IQueryValue>, props?: unknown) => (dispatch: Dispatch) => Promise<void>,
    validateQuery: (query: Record<string, IQueryValue>) => Record<string, IQueryValue> = () => ({}), // is empty object (has no attributes) by default
    includes: string[] | null = null, // null -> include all attributes by default
    excludes: string[] | null = null // null -> exclude no attributes by default
) => {
    const dispatch = useDispatch();
    const location = useLocation();
    const history = useHistory();

    const {requestState, latestQuery} = useSelector(stateSelector, shallowEqual);

    const selectAttributes = (query: Record<string, unknown>): Record<string, unknown> => {
        const picked = includes === null ? query : pick(query, includes);
        return excludes === null ? picked : omit(picked, excludes);
    };

    useEffect(() => {
        const query = parseSearch(location.search);

        const finalQuery = {
            ...query,
            ...validateQuery(query) // append validated URL parameters
        };

        if (!isEqual(query, finalQuery)) {
            return history.push(appendQueryString(location.pathname, finalQuery));
        }
        if (
            requestState === RequestState.None || // request state is not valid
            isNull(latestQuery) || // latest is not valid
            !isEqual(selectAttributes(latestQuery), selectAttributes(query)) // URL params differ with the latest one
        ) {
            dispatch(fetchAction(query));
        }
    }, [location.search]);
};
